/**
 * ImportSyncPage — ייבוא וסנכרון מוצרים
 *
 * Phase 49: Universal Selective Product Import Wizard
 * Phase 77: SKU-based diff — classify products as new / updated / unchanged
 *
 * Route:  /app/inventory/import
 * Guard:  module="inventory" level="editor"
 */

import React, { useEffect, useState, useCallback, useMemo, useRef } from 'react';
import { Download, RefreshCw, Search, SquareCheck as CheckSquare, Square, ChevronDown, Loader as Loader2, CircleAlert as AlertCircle, CircleCheck as CheckCircle2, Package, ImageOff, FileSpreadsheet, ShoppingBag, Info, Circle as XCircle, TriangleAlert, Sparkles, X, Hash, Percent, ToggleLeft, ToggleRight, Plus, RefreshCcw, Minus, ArrowRight, Image, Tag, Copy } from 'lucide-react';
import { supabase } from '@/lib/supabase';
import { useCompany } from '@/context/CompanyContext';
import {
  fetchCompanySkuSettings,
  allocateSequentialSkus,
} from '@/pages/inventory/SkuSettingsTab';

// ─── Types ────────────────────────────────────────────────────────────────────

interface SourceProduct {
  external_id:  string;
  name:         string;
  sku:          string;
  barcode:      string | null;
  price:        number;
  cost:         number | null;
  image_url:    string | null;
  description?: string | null;
  images?:      { url: string; alt: string | null }[];
  supplier_id:  string | null;
  status?:      string;
}

interface ImportResult {
  job_id:         string;
  imported_count: number;
  skipped_count:  number;
  total_selected: number;
}

type PreflightScenario = 'clean' | 'A' | 'B' | 'AB';

interface PreflightResult {
  scenario:   PreflightScenario;
  withSku:    SourceProduct[];
  withoutSku: SourceProduct[];
}

/** Existing record from inventory_products matching by SKU */
interface ExistingProduct {
  id:        string;
  sku:       string;
  name:      string;
  price:     number;
  image_url: string | null;
  cost:      number | null;
}

/** Which fields differ between source and existing */
type ChangedField = 'price' | 'image_url' | 'name' | 'cost';

/** Classified product after SKU-diff */
interface ClassifiedProduct {
  source:    SourceProduct;
  status:    'new' | 'updated' | 'unchanged';
  existing?: ExistingProduct;
  changes?:  ChangedField[];
}

interface SourceDef {
  id:          string;
  label:       string;
  icon:        React.ReactNode;
  available:   boolean;
  comingSoon?: boolean;
  fetch:       (companyId: string, accessToken: string) => Promise<SourceProduct[]>;
}

type FetchState    = 'idle' | 'loading' | 'done' | 'error';
type ImportState   = 'idle' | 'importing' | 'done' | 'error';
type PreflightStep = 'none' | 'scenarioA' | 'scenarioB' | 'generating';

// ─── Helpers ──────────────────────────────────────────────────────────────────

const fmtPrice = (n: number) =>
  new Intl.NumberFormat('he-IL', {
    style:                 'currency',
    currency:              'ILS',
    maximumFractionDigits: 2,
  }).format(n);

const SUPABASE_URL =
  (import.meta as unknown as { env: Record<string, string> }).env.VITE_SUPABASE_URL ?? '';

// ─── Source Adapters ──────────────────────────────────────────────────────────

async function fetchFromShopify(companyId: string, accessToken: string): Promise<SourceProduct[]> {
  const res = await fetch(`${SUPABASE_URL}/functions/v1/shopify-fetch-products`, {
    method:  'POST',
    headers: {
      'Content-Type':  'application/json',
      'Authorization': `Bearer ${accessToken}`,
    },
    body: JSON.stringify({ company_id: companyId }),
  });

  const json = await res.json() as {
    products?: Array<{
      shopify_id:         number;
      shopify_variant_id: number;
      title:              string;
      sku:                string;
      price:              number;
      cost:               number | null;
      image_url:          string | null;
      description?:       string | null;
      images?:            { url: string; alt: string | null }[];
      status?:            string;
      is_simple?:         boolean;
    }>;
    error?: string;
  };

  if (!res.ok) throw new Error(json.error ?? `שגיאת שרת (HTTP ${res.status})`);

  return (json.products ?? []).map(p => ({
    external_id: p.is_simple ? `${p.shopify_id}` : `${p.shopify_id}_${p.shopify_variant_id}`,
    name:        p.title,
    sku:         p.sku ?? '',
    barcode:     null,
    price:       p.price,
    cost:        p.cost,
    image_url:   p.image_url,
    description: p.description ?? null,
    images:      p.images ?? [],
    supplier_id: null,
    status:      p.status,
  }));
}

function buildSources(): SourceDef[] {
  return [
    {
      id:        'shopify',
      label:     'Shopify',
      icon:      <ShoppingBag className="w-4 h-4" />,
      available: true,
      fetch:     fetchFromShopify,
    },
    {
      id:          'excel',
      label:       'Excel / CSV',
      icon:        <FileSpreadsheet className="w-4 h-4" />,
      available:   false,
      comingSoon:  true,
      fetch:       async () => { throw new Error('בקרוב'); },
    },
  ];
}

// ─── SKU diff helper ──────────────────────────────────────────────────────────

function classifyProducts(
  sources:  SourceProduct[],
  existing: ExistingProduct[],
): ClassifiedProduct[] {
  const bySkuMap = new Map<string, ExistingProduct>();
  for (const e of existing) {
    bySkuMap.set(e.sku.trim().toLowerCase(), e);
  }

  return sources.map(src => {
    const normSku = src.sku.trim().toLowerCase();
    const ex = normSku ? bySkuMap.get(normSku) : undefined;

    if (!ex) return { source: src, status: 'new' };

    const changes: ChangedField[] = [];
    if (Math.abs(src.price - Number(ex.price)) > 0.001) changes.push('price');
    if ((src.image_url ?? null) !== (ex.image_url ?? null))  changes.push('image_url');
    if (src.name.trim() !== ex.name.trim())                   changes.push('name');
    if (src.cost != null && ex.cost != null &&
        Math.abs(src.cost - Number(ex.cost)) > 0.001)         changes.push('cost');

    if (changes.length > 0) {
      return { source: src, status: 'updated', existing: ex, changes };
    }
    return { source: src, status: 'unchanged', existing: ex, changes: [] };
  });
}

// ─── Field change badge ───────────────────────────────────────────────────────

const FIELD_LABELS: Record<ChangedField, { label: string; icon: React.ReactNode }> = {
  price:     { label: 'מחיר',   icon: <Tag       className="w-3 h-3" /> },
  image_url: { label: 'תמונה',  icon: <Image     className="w-3 h-3" /> },
  name:      { label: 'שם',     icon: <ArrowRight className="w-3 h-3" /> },
  cost:      { label: 'עלות',   icon: <Minus     className="w-3 h-3" /> },
};

function ChangeBadge({ field }: { field: ChangedField }) {
  const { label, icon } = FIELD_LABELS[field];
  return (
    <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-xs
                     bg-amber-100 text-amber-700 border border-amber-200 font-medium">
      {icon}{label}
    </span>
  );
}

// ─── ProductRow ───────────────────────────────────────────────────────────────

function ProductRow({
  classified,
  checked,
  onToggle,
  isDuplicate,
}: {
  classified:   ClassifiedProduct;
  checked:      boolean;
  onToggle:     () => void;
  isDuplicate?: boolean;
}) {
  const { source: product, status, existing, changes } = classified;
  const [imgErr, setImgErr] = useState(false);

  const rowBg = isDuplicate
    ? 'bg-red-50/60'
    : checked
    ? status === 'new'      ? 'bg-emerald-50'
    : status === 'updated'  ? 'bg-amber-50'
    : 'bg-gray-50'
    : 'bg-white';

  return (
    <tr
      onClick={onToggle}
      className={`cursor-pointer transition-colors border-b border-gray-100 hover:brightness-95 ${rowBg}`}
    >
      {/* Checkbox */}
      <td className="w-10 px-3 py-3 text-center">
        <div className="flex items-center justify-center">
          {checked
            ? <CheckSquare className="w-5 h-5 text-blue-600" />
            : <Square       className="w-5 h-5 text-gray-300" />}
        </div>
      </td>

      {/* Image */}
      <td className="w-14 px-2 py-2">
        <div className="relative">
          {product.image_url && !imgErr ? (
            <img
              src={product.image_url}
              alt={product.name}
              onError={() => setImgErr(true)}
              className={`w-10 h-10 object-cover rounded-lg border shadow-sm
                          ${changes?.includes('image_url') ? 'ring-2 ring-amber-400' : 'border-gray-200'}`}
            />
          ) : (
            <div className="w-10 h-10 rounded-lg bg-gray-100 flex items-center justify-center border border-gray-200">
              <ImageOff className="w-4 h-4 text-gray-300" />
            </div>
          )}
          {changes?.includes('image_url') && (
            <span className="absolute -top-1 -right-1 w-3 h-3 bg-amber-400 rounded-full border border-white" />
          )}
        </div>
      </td>

      {/* Name */}
      <td className="px-4 py-3">
        <p className={`font-medium text-sm leading-snug
                       ${changes?.includes('name') ? 'text-amber-800' : 'text-gray-900'}`}>
          {product.name}
          {changes?.includes('name') && existing && (
            <span className="block text-xs text-gray-400 font-normal line-through mt-0.5">
              {existing.name}
            </span>
          )}
        </p>
        {product.status && product.status !== 'active' && (
          <span className="inline-block mt-0.5 px-1.5 py-0.5 text-xs rounded-full bg-amber-100 text-amber-700">
            {product.status}
          </span>
        )}
        {/* Change badges */}
        {changes && changes.length > 0 && (
          <div className="flex flex-wrap gap-1 mt-1">
            {changes.map(f => <ChangeBadge key={f} field={f} />)}
          </div>
        )}
      </td>

      {/* SKU */}
      <td className="px-4 py-3">
        <div className="flex items-center gap-1.5 flex-wrap">
          <span className={`font-mono text-xs px-2 py-1 rounded ${isDuplicate ? 'text-red-700 bg-red-100 border border-red-200' : 'text-gray-500 bg-gray-100'}`}>
            {product.sku || '—'}
          </span>
          {isDuplicate && (
            <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[10px] bg-red-100 text-red-600 border border-red-200 font-bold">
              <Copy className="w-3 h-3" />כפול
            </span>
          )}
        </div>
      </td>

      {/* Price */}
      <td className="px-4 py-3 text-right">
        <span className={`text-sm font-semibold
                          ${changes?.includes('price') ? 'text-amber-700' : 'text-gray-800'}`}>
          {fmtPrice(product.price)}
        </span>
        {changes?.includes('price') && existing && (
          <p className="text-xs text-gray-400 mt-0.5 line-through">{fmtPrice(Number(existing.price))}</p>
        )}
        {product.cost != null && !changes?.includes('price') && (
          <p className="text-xs text-gray-400 mt-0.5">עלות: {fmtPrice(product.cost)}</p>
        )}
      </td>
    </tr>
  );
}

// ─── Section header ───────────────────────────────────────────────────────────

function SectionHeader({
  icon,
  label,
  count,
  color,
}: {
  icon:        React.ReactNode;
  label:       string;
  count:       number;
  color:       string;
}) {
  return (
    <tr className={`border-b border-gray-200 ${color}`}>
      <td colSpan={5} className="px-4 py-2.5">
        <div className="flex items-center gap-2">
          {icon}
          <span className="text-sm font-bold">{label}</span>
          <span className="px-2 py-0.5 rounded-full text-xs font-bold bg-white/60">
            {count}
          </span>
        </div>
      </td>
    </tr>
  );
}

// ─── Group Toggle Chip ───────────────────────────────────────────────────────

function GroupChip({
  label,
  count,
  allSelected,
  colorActive,
  colorInactive,
  onClick,
}: {
  label:         string;
  count:         number;
  allSelected:   boolean;
  colorActive:   string;
  colorInactive: string;
  onClick:       () => void;
}) {
  return (
    <button
      onClick={onClick}
      className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-semibold border transition-all duration-150 ${
        allSelected ? colorActive : colorInactive
      }`}
    >
      {allSelected
        ? <CheckSquare className="w-3 h-3" />
        : <Square className="w-3 h-3" />}
      {label} ({count})
    </button>
  );
}

// ─── Import Action Button ─────────────────────────────────────────────────────

function ImportButton({
  selectedCount,
  isImporting,
  generatingSku,
  onClick,
  size = 'md',
}: {
  selectedCount: number;
  isImporting:   boolean;
  generatingSku: boolean;
  onClick:       () => void;
  size?:         'sm' | 'md';
}) {
  const disabled = selectedCount === 0 || isImporting || generatingSku;
  const px = size === 'sm' ? 'px-4 py-2' : 'px-6 py-2.5';

  return (
    <button
      onClick={onClick}
      disabled={disabled}
      className={`flex items-center gap-2 ${px} rounded-xl text-sm font-bold shadow-sm transition-all
                  ${disabled
                    ? 'bg-gray-200 text-gray-400 cursor-not-allowed'
                    : 'bg-blue-600 hover:bg-blue-700 text-white'}
                  disabled:opacity-50 disabled:cursor-not-allowed`}
    >
      {isImporting
        ? <><Loader2 className="w-4 h-4 animate-spin" />מייבא…</>
        : <><Download className="w-4 h-4" />
            {selectedCount > 0
              ? `ייבא / עדכן ${selectedCount} מוצרים`
              : 'בחר מוצרים לייבוא'}</>}
    </button>
  );
}

// ─── Main Page ────────────────────────────────────────────────────────────────

export function ImportSyncPage() {
  const { activeCompanyId } = useCompany();
  const SOURCES = useMemo(() => buildSources(), []);

  // ── State ──────────────────────────────────────────────────────────────────
  const [activeSourceId, setActiveSourceId] = useState<string>('shopify');
  const [sourceDropOpen, setSourceDropOpen]  = useState(false);
  const dropRef = useRef<HTMLDivElement>(null);

  const [fetchState,  setFetchState]  = useState<FetchState>('idle');
  const [fetchError,  setFetchError]  = useState<string | null>(null);
  const [products,    setProducts]    = useState<SourceProduct[]>([]);

  /** Classified products after SKU-diff — computed once after fetch+lookup */
  const [classified,  setClassified]  = useState<ClassifiedProduct[]>([]);
  const [diffLoading, setDiffLoading] = useState(false);

  const [search,       setSearch]       = useState('');
  const [selected,     setSelected]     = useState<Set<string>>(new Set());

  const [importState,  setImportState]  = useState<ImportState>('idle');
  const [importResult, setImportResult] = useState<ImportResult | null>(null);
  const [importError,  setImportError]  = useState<string | null>(null);

  const [preflightStep,   setPreflightStep]   = useState<PreflightStep>('none');
  const [preflightResult, setPreflightResult] = useState<PreflightResult | null>(null);
  const [resolvedBatch,   setResolvedBatch]   = useState<SourceProduct[]>([]);
  const [generatingSku,   setGeneratingSku]   = useState(false);

  const [priceInclVat, setPriceInclVat] = useState(false);

  const activeSource = useMemo(
    () => SOURCES.find(s => s.id === activeSourceId) ?? SOURCES[0]!,
    [SOURCES, activeSourceId],
  );

  // ── Close dropdown on outside click ───────────────────────────────────────
  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (dropRef.current && !dropRef.current.contains(e.target as Node)) {
        setSourceDropOpen(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  // ── Reset on source change ─────────────────────────────────────────────────
  const handleSourceChange = (sourceId: string) => {
    setActiveSourceId(sourceId);
    setSourceDropOpen(false);
    setProducts([]);
    setClassified([]);
    setSelected(new Set());
    setFetchState('idle');
    setFetchError(null);
    setImportState('idle');
    setImportResult(null);
    setImportError(null);
    setSearch('');
    setPreflightStep('none');
    setPreflightResult(null);
    setResolvedBatch([]);
    setPriceInclVat(false);
  };

  // ── Fetch existing products by SKU ─────────────────────────────────────────
  const fetchExisting = useCallback(async (skus: string[]): Promise<ExistingProduct[]> => {
    if (!activeCompanyId || skus.length === 0) return [];
    const nonEmpty = skus.filter(s => s.trim() !== '');
    if (nonEmpty.length === 0) return [];

    const { data } = await supabase
      .from('inventory_products')
      .select('id, sku, name, price, image_url, cost')
      .eq('company_id', activeCompanyId)
      .in('sku', nonEmpty);

    return (data ?? []) as ExistingProduct[];
  }, [activeCompanyId]);

  // ── Fetch from source ──────────────────────────────────────────────────────
  const handleFetch = useCallback(async () => {
    if (!activeCompanyId) return;
    setFetchState('loading');
    setFetchError(null);
    setProducts([]);
    setClassified([]);
    setSelected(new Set());
    setImportState('idle');
    setImportResult(null);
    setImportError(null);
    setPreflightStep('none');
    setPreflightResult(null);
    setResolvedBatch([]);

    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) throw new Error('לא מחובר — אנא התחבר מחדש');

      const fetched = await activeSource.fetch(activeCompanyId, session.access_token);
      setProducts(fetched);
      setFetchState('done');

      // ── SKU diff ──
      setDiffLoading(true);
      try {
        const skus     = fetched.map(p => p.sku).filter(s => s.trim() !== '');
        const existing = await fetchExisting(skus);
        const result   = classifyProducts(fetched, existing);
        setClassified(result);

        // Default selection: new + updated only (not unchanged), excluding duplicates
        const dupCounts = new Map<string, string[]>();
        for (const c of result) {
          const norm = c.source.sku.trim().toLowerCase();
          if (!norm) continue;
          const arr = dupCounts.get(norm) ?? [];
          arr.push(c.source.external_id);
          dupCounts.set(norm, arr);
        }
        const dupIds = new Set<string>();
        for (const ids of dupCounts.values()) {
          if (ids.length > 1) ids.forEach(id => dupIds.add(id));
        }

        const autoSelect = new Set(
          result
            .filter(c => (c.status === 'new' || c.status === 'updated') && !dupIds.has(c.source.external_id))
            .map(c => c.source.external_id),
        );
        setSelected(autoSelect);
      } finally {
        setDiffLoading(false);
      }
    } catch (e) {
      setFetchError(e instanceof Error ? e.message : 'שגיאה לא ידועה בטעינת המוצרים');
      setFetchState('error');
      setDiffLoading(false);
    }
  }, [activeCompanyId, activeSource, fetchExisting]);

  // ── Filtered + grouped ─────────────────────────────────────────────────────
  const filtered = useMemo(() => {
    if (!search.trim()) return classified;
    const q = search.toLowerCase();
    return classified.filter(
      c =>
        c.source.name.toLowerCase().includes(q) ||
        c.source.sku.toLowerCase().includes(q),
    );
  }, [classified, search]);

  const newProducts       = useMemo(() => filtered.filter(c => c.status === 'new'),       [filtered]);
  const updatedProducts   = useMemo(() => filtered.filter(c => c.status === 'updated'),   [filtered]);
  const unchangedProducts = useMemo(() => filtered.filter(c => c.status === 'unchanged'), [filtered]);

  const draftProducts    = useMemo(() => filtered.filter(c => c.source.status === 'draft'),    [filtered]);
  const archivedProducts = useMemo(() => filtered.filter(c => c.source.status === 'archived'), [filtered]);

  // ── Duplicate SKU detection ─────────────────────────────────────────────
  const duplicateSkuMap = useMemo(() => {
    const counts = new Map<string, string[]>();
    for (const c of classified) {
      const norm = c.source.sku.trim().toLowerCase();
      if (!norm) continue;
      const arr = counts.get(norm) ?? [];
      arr.push(c.source.external_id);
      counts.set(norm, arr);
    }
    const dupes = new Map<string, string>();
    for (const [sku, ids] of counts) {
      if (ids.length > 1) {
        for (const id of ids) dupes.set(id, sku);
      }
    }
    return dupes;
  }, [classified]);

  const duplicateSkuNames = useMemo(() => {
    if (duplicateSkuMap.size === 0) return [];
    const seen = new Set<string>();
    const result: { sku: string; names: string[] }[] = [];
    for (const c of classified) {
      const norm = c.source.sku.trim().toLowerCase();
      if (!norm || !duplicateSkuMap.has(c.source.external_id) || seen.has(norm)) continue;
      seen.add(norm);
      const names = classified
        .filter(x => x.source.sku.trim().toLowerCase() === norm)
        .map(x => x.source.name);
      result.push({ sku: c.source.sku, names });
    }
    return result;
  }, [classified, duplicateSkuMap]);

  // ── Selection helpers ──────────────────────────────────────────────────────
  const toggleOne = (id: string) => {
    setSelected(prev => {
      const next = new Set(prev);
      if (next.has(id)) {
        next.delete(id);
      } else {
        // If this product has a duplicate SKU, deselect the other duplicate(s)
        const dupSku = duplicateSkuMap.get(id);
        if (dupSku) {
          for (const c of classified) {
            if (c.source.external_id !== id && c.source.sku.trim().toLowerCase() === dupSku) {
              next.delete(c.source.external_id);
            }
          }
        }
        next.add(id);
      }
      return next;
    });
  };

  const toggleGroup = (items: ClassifiedProduct[]) => {
    const ids        = items.map(c => c.source.external_id);
    const allChosen  = ids.every(id => selected.has(id));
    setSelected(prev => {
      const next = new Set(prev);
      if (allChosen) ids.forEach(id => next.delete(id));
      else           ids.forEach(id => next.add(id));
      return next;
    });
  };

  const allFilteredSelected =
    filtered.length > 0 && filtered.every(c => selected.has(c.source.external_id));

  const toggleAll = () => {
    if (allFilteredSelected) {
      setSelected(prev => {
        const next = new Set(prev);
        filtered.forEach(c => next.delete(c.source.external_id));
        return next;
      });
    } else {
      setSelected(prev => {
        const next = new Set(prev);
        filtered.forEach(c => next.add(c.source.external_id));
        return next;
      });
    }
  };

  const clearSelection = () => setSelected(new Set());

  const selectedCount = useMemo(
    () => products.filter(p => selected.has(p.external_id)).length,
    [products, selected],
  );

  // ── doImport ───────────────────────────────────────────────────────────────
  const doImport = useCallback(async (batch: SourceProduct[]) => {
    if (!activeCompanyId || batch.length === 0) return;

    setPreflightStep('none');
    setImportState('importing');
    setImportError(null);
    setImportResult(null);

    const payload = batch.map(p => ({
      external_id: p.external_id,
      name:        p.name,
      sku:         p.sku || `ext-${p.external_id}`,
      barcode:     p.barcode ?? null,
      price:       p.price,
      cost:        p.cost ?? null,
      image_url:   p.image_url ?? null,
      description: p.description ?? null,
      images:      p.images ?? [],
      supplier_id: p.supplier_id ?? null,
    }));

    try {
      const { data, error } = await supabase.rpc('import_selected_products', {
        p_company_id:     activeCompanyId,
        p_source:         activeSourceId,
        p_products:       payload,
        p_price_incl_vat: priceInclVat,
      });

      if (error) throw new Error(`${error.message} [${error.code}]`);

      const result = data as ImportResult;
      setImportResult(result);
      setImportState('done');

      // Re-run diff after successful import so statuses refresh
      const skus     = products.map(p => p.sku).filter(s => s.trim() !== '');
      const existing = await fetchExisting(skus);
      setClassified(classifyProducts(products, existing));
    } catch (e) {
      setImportError(e instanceof Error ? e.message : 'שגיאה לא ידועה בייבוא');
      setImportState('error');
    }
  }, [activeCompanyId, activeSourceId, priceInclVat, products, fetchExisting]);

  // ── handleImportClick ──────────────────────────────────────────────────────
  const handleImportClick = useCallback(() => {
    if (!activeCompanyId || selectedCount === 0) return;

    const toImport = products.filter(p => selected.has(p.external_id));

    const withSku    = toImport.filter(p => p.sku && p.sku.trim() !== '');
    const withoutSku = toImport.filter(p => !p.sku || p.sku.trim() === '');

    const hasExternalSkus = withSku.length > 0;
    const hasMissingSkus  = withoutSku.length > 0;

    let scenario: PreflightScenario = 'clean';
    if (hasExternalSkus && hasMissingSkus) scenario = 'AB';
    else if (hasExternalSkus)              scenario = 'A';
    else if (hasMissingSkus)               scenario = 'B';

    const result: PreflightResult = { scenario, withSku, withoutSku };
    setPreflightResult(result);

    if (scenario === 'clean') {
      doImport(toImport);
      return;
    }

    setResolvedBatch(toImport);

    if (scenario === 'A') {
      setPreflightStep('scenarioA');
    } else {
      setPreflightStep('scenarioB');
    }
  }, [activeCompanyId, products, selected, selectedCount, doImport]);

  const handleScenarioAConfirm = useCallback(() => {
    doImport(resolvedBatch);
  }, [resolvedBatch, doImport]);

  const handleScenarioBGenerate = useCallback(async () => {
    if (!activeCompanyId || !preflightResult) return;

    setGeneratingSku(true);
    setPreflightStep('generating');

    try {
      const { withoutSku } = preflightResult;
      const count = withoutSku.length;

      const skuSettings = await fetchCompanySkuSettings(activeCompanyId);
      const method = skuSettings?.sku_generation_method ?? 'sequential';

      let generatedSkus: string[];
      if (method === 'sequential' || method === 'prefixed') {
        generatedSkus = await allocateSequentialSkus(activeCompanyId, count);
      } else {
        generatedSkus = await allocateSequentialSkus(activeCompanyId, count);
      }

      const patchedWithout = withoutSku.map((p, i) => ({
        ...p,
        sku: generatedSkus[i] ?? `gen-${i + 1}`,
      }));

      const originalOrder = resolvedBatch.map(p => {
        if (p.sku && p.sku.trim() !== '') return p;
        const patched = patchedWithout.find(pp => pp.external_id === p.external_id);
        return patched ?? p;
      });

      await doImport(originalOrder);
    } catch (e) {
      setImportError(e instanceof Error ? e.message : 'שגיאה ביצירת מק״טים אוטומטית');
      setImportState('error');
      setPreflightStep('none');
    } finally {
      setGeneratingSku(false);
    }
  }, [activeCompanyId, preflightResult, resolvedBatch, doImport]);

  const handleScenarioBSkip = useCallback(() => {
    if (!preflightResult) return;
    doImport(preflightResult.withSku);
  }, [preflightResult, doImport]);

  // ── Derived ────────────────────────────────────────────────────────────────
  const isFetching  = fetchState  === 'loading';
  const isImporting = importState === 'importing';

  const diffSummary = useMemo(() => {
    if (classified.length === 0) return null;
    const n = classified.filter(c => c.status === 'new').length;
    const u = classified.filter(c => c.status === 'updated').length;
    const s = classified.filter(c => c.status === 'unchanged').length;
    return { new: n, updated: u, unchanged: s };
  }, [classified]);

  // ── Render ─────────────────────────────────────────────────────────────────

  return (
    <div className="min-h-screen bg-gray-50" dir="rtl">
      <div className="max-w-6xl mx-auto px-4 py-8 space-y-6">

        {/* ── Page header ── */}
        <div className="flex items-start justify-between gap-4 flex-wrap">
          <div>
            <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
              <Download className="w-6 h-6 text-blue-600" />
              ייבוא וסנכרון מוצרים
            </h1>
            <p className="text-sm text-gray-500 mt-1">
              ייבא מוצרים ממקורות חיצוניים ישירות לקטלוג המלאי שלך.
            </p>
          </div>

          {/* Right-side controls */}
          <div className="flex items-center gap-3 flex-wrap">
            {/* VAT toggle */}
            <button
              type="button"
              onClick={() => setPriceInclVat(v => !v)}
              title="האם המחירים המיובאים כוללים מע״מ?"
              className={`flex items-center gap-2 px-4 py-2.5 rounded-xl border text-sm font-medium
                          shadow-sm transition-all select-none
                          ${priceInclVat
                            ? 'bg-blue-50 border-blue-400 text-blue-700'
                            : 'bg-white border-gray-300 text-gray-600 hover:bg-gray-50'}`}
            >
              <Percent className="w-4 h-4 shrink-0" />
              <span>מחיר כולל מע״מ?</span>
              {priceInclVat
                ? <ToggleRight className="w-5 h-5 text-blue-600" />
                : <ToggleLeft  className="w-5 h-5 text-gray-300" />}
              {priceInclVat && (
                <span className="text-xs font-bold text-blue-600 bg-blue-100 px-1.5 py-0.5 rounded-full">
                  ÷(1+מע״מ)
                </span>
              )}
            </button>

            {/* Source picker */}
            <div className="relative" ref={dropRef}>
              <button
                onClick={() => setSourceDropOpen(o => !o)}
                className="flex items-center gap-2.5 px-4 py-2.5 bg-white border border-gray-300
                           rounded-xl text-sm font-medium text-gray-700 shadow-sm
                           hover:bg-gray-50 transition-colors min-w-[200px] justify-between"
              >
                <div className="flex items-center gap-2">
                  {activeSource.icon}
                  <span>{activeSource.label}</span>
                  {activeSource.comingSoon && (
                    <span className="text-xs text-gray-400">(בקרוב)</span>
                  )}
                </div>
                <ChevronDown className="w-4 h-4 text-gray-400" />
              </button>

              {sourceDropOpen && (
                <div className="absolute top-full mt-1 right-0 w-56 bg-white rounded-xl
                                border border-gray-200 shadow-xl z-20 overflow-hidden">
                  <div className="p-1.5 space-y-0.5">
                    <p className="px-3 py-1.5 text-xs font-semibold text-gray-400 uppercase tracking-wide">
                      מקור נתונים
                    </p>
                    {SOURCES.map(src => (
                      <button
                        key={src.id}
                        onClick={() => !src.comingSoon && handleSourceChange(src.id)}
                        disabled={!!src.comingSoon}
                        className={`w-full flex items-center gap-2.5 px-3 py-2.5 rounded-lg text-sm
                                    transition-colors text-right
                                    ${src.id === activeSourceId
                                      ? 'bg-blue-50 text-blue-700 font-medium'
                                      : src.comingSoon
                                        ? 'text-gray-300 cursor-not-allowed'
                                        : 'text-gray-700 hover:bg-gray-50'}`}
                      >
                        {src.icon}
                        <span className="flex-1">{src.label}</span>
                        {src.comingSoon && (
                          <span className="text-xs bg-gray-100 text-gray-400 px-1.5 py-0.5 rounded-full">
                            בקרוב
                          </span>
                        )}
                        {src.id === activeSourceId && !src.comingSoon && (
                          <CheckCircle2 className="w-4 h-4 text-blue-600" />
                        )}
                      </button>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* ── Info banner for Excel placeholder ── */}
        {activeSource.comingSoon && (
          <div className="flex items-center gap-3 px-4 py-3 bg-amber-50 border border-amber-200 rounded-xl text-sm text-amber-700">
            <Info className="w-4 h-4 shrink-0" />
            מקור זה יהיה זמין בגרסה הבאה. בחר Shopify כדי לייבא כרגע.
          </div>
        )}

        {/* ── Main card ── */}
        {!activeSource.comingSoon && (
          <div className="bg-white rounded-2xl border border-gray-200 shadow-sm overflow-hidden">

            {/* Toolbar */}
            <div className="flex items-center justify-between gap-3 px-5 py-4 border-b border-gray-100 flex-wrap">
              <div className="flex items-center gap-3">
                <button
                  onClick={handleFetch}
                  disabled={isFetching || isImporting}
                  className="flex items-center gap-2 px-4 py-2 bg-gray-900 hover:bg-gray-700
                             text-white text-sm font-semibold rounded-xl transition-colors
                             disabled:opacity-50 disabled:cursor-not-allowed shadow-sm"
                >
                  {isFetching
                    ? <><Loader2 className="w-4 h-4 animate-spin" />טוען…</>
                    : <><RefreshCw className="w-4 h-4" />
                        {fetchState === 'done' ? 'רענן מוצרים' : 'טען מוצרים'}</>}
                </button>

                {fetchState === 'done' && !diffLoading && (
                  <span className="px-2.5 py-1 bg-gray-100 text-gray-600 text-xs font-semibold rounded-full">
                    {products.length} מוצרים
                  </span>
                )}

                {diffLoading && (
                  <span className="flex items-center gap-1.5 text-xs text-gray-400">
                    <Loader2 className="w-3.5 h-3.5 animate-spin" />
                    בודק מוצרים קיימים…
                  </span>
                )}
              </div>

              {/* Search */}
              {fetchState === 'done' && products.length > 0 && (
                <div className="flex items-center gap-2 flex-1 max-w-xs">
                  <div className="relative flex-1">
                    <Search className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400 pointer-events-none" />
                    <input
                      type="text"
                      value={search}
                      onChange={e => setSearch(e.target.value)}
                      placeholder="חיפוש לפי שם / מקט…"
                      className="w-full pr-9 pl-3 py-2 text-sm border border-gray-300 rounded-xl
                                 focus:outline-none focus:ring-2 focus:ring-blue-400"
                      dir="rtl"
                    />
                    {search && (
                      <button
                        onClick={() => setSearch('')}
                        className="absolute left-2.5 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                      >
                        <XCircle className="w-4 h-4" />
                      </button>
                    )}
                  </div>
                </div>
              )}
            </div>

            {/* ── Idle ── */}
            {fetchState === 'idle' && (
              <div className="flex flex-col items-center justify-center py-20 gap-4 text-center px-6">
                <div className="w-16 h-16 rounded-2xl bg-blue-50 flex items-center justify-center">
                  <Package className="w-8 h-8 text-blue-400" />
                </div>
                <div>
                  <p className="font-semibold text-gray-700">לחץ "טען מוצרים" להתחיל</p>
                  <p className="text-sm text-gray-400 mt-1">
                    המוצרים יטענו מ-{activeSource.label} — המערכת תבדוק אילו מוצרים כבר קיימים.
                  </p>
                </div>
              </div>
            )}

            {/* ── Loading ── */}
            {fetchState === 'loading' && (
              <div className="flex items-center justify-center py-20 gap-3 text-gray-400">
                <Loader2 className="w-6 h-6 animate-spin" />
                <span className="text-sm">טוען מוצרים מ-{activeSource.label}…</span>
              </div>
            )}

            {/* ── Fetch error ── */}
            {fetchState === 'error' && fetchError && (
              <div className="m-5 flex items-start gap-3 px-4 py-4 bg-red-50 border border-red-200 rounded-xl text-sm text-red-700">
                <AlertCircle className="w-5 h-5 shrink-0 mt-0.5" />
                <div>
                  <p className="font-semibold mb-1">שגיאה בטעינת המוצרים</p>
                  <p className="text-red-600">{fetchError}</p>
                  <p className="text-red-500 text-xs mt-2">
                    ודא שחנות ה-Shopify מחוברת בהגדרות האינטגרציות.
                  </p>
                </div>
              </div>
            )}

            {/* ── Product table ── */}
            {fetchState === 'done' && classified.length > 0 && (
              <>
                {/* ── Diff summary cards ── */}
                {diffSummary && !diffLoading && (
                  <div className="grid grid-cols-3 gap-3 px-5 pt-4 pb-3">
                    <div className="flex items-center gap-2.5 px-3 py-2.5 rounded-xl bg-emerald-50 border border-emerald-200">
                      <div className="p-1.5 bg-emerald-500 rounded-lg">
                        <Plus className="w-3.5 h-3.5 text-white" />
                      </div>
                      <div>
                        <p className="text-xs text-emerald-600 font-medium">מוצרים חדשים</p>
                        <p className="text-lg font-bold text-emerald-700 leading-none">{diffSummary.new}</p>
                      </div>
                    </div>
                    <div className="flex items-center gap-2.5 px-3 py-2.5 rounded-xl bg-amber-50 border border-amber-200">
                      <div className="p-1.5 bg-amber-500 rounded-lg">
                        <RefreshCcw className="w-3.5 h-3.5 text-white" />
                      </div>
                      <div>
                        <p className="text-xs text-amber-600 font-medium">עם עדכונים</p>
                        <p className="text-lg font-bold text-amber-700 leading-none">{diffSummary.updated}</p>
                      </div>
                    </div>
                    <div className="flex items-center gap-2.5 px-3 py-2.5 rounded-xl bg-gray-50 border border-gray-200">
                      <div className="p-1.5 bg-gray-400 rounded-lg">
                        <CheckCircle2 className="w-3.5 h-3.5 text-white" />
                      </div>
                      <div>
                        <p className="text-xs text-gray-500 font-medium">ללא שינוי</p>
                        <p className="text-lg font-bold text-gray-600 leading-none">{diffSummary.unchanged}</p>
                      </div>
                    </div>
                  </div>
                )}

                {/* ── Duplicate SKU warning banner ── */}
                {duplicateSkuNames.length > 0 && (
                  <div className="mx-5 mt-4 flex items-start gap-3 px-4 py-4 bg-red-50 border border-red-200 rounded-xl text-sm text-red-700" dir="rtl">
                    <Copy className="w-5 h-5 shrink-0 mt-0.5 text-red-500" />
                    <div>
                      <p className="font-bold mb-1">נמצאו מוצרים עם מק״ט כפול — לא ניתן לייבא את שניהם</p>
                      <p className="text-red-600 text-xs mb-2">
                        יש לשנות את המק״ט של אחד המוצרים בשופיפיי, או לבחור רק אחד מכל זוג לייבוא.
                      </p>
                      <ul className="space-y-1.5">
                        {duplicateSkuNames.map(d => (
                          <li key={d.sku} className="flex items-start gap-2">
                            <span className="font-mono text-xs bg-red-100 px-2 py-0.5 rounded shrink-0">{d.sku}</span>
                            <span className="text-xs text-red-600">{d.names.join(' / ')}</span>
                          </li>
                        ))}
                      </ul>
                    </div>
                  </div>
                )}

                {/* ── Top action bar ── */}
                <div className="flex items-center justify-between gap-3 px-5 py-3 border-y border-gray-100 bg-gray-50 flex-wrap">
                  <div className="flex items-center gap-2 flex-wrap">
                    {/* Select All / Clear */}
                    <button
                      onClick={toggleAll}
                      className="flex items-center gap-1.5 text-sm text-blue-600 hover:text-blue-800 font-medium transition-colors"
                    >
                      {allFilteredSelected
                        ? <><CheckSquare className="w-4 h-4" />בטל הכל</>
                        : <><Square       className="w-4 h-4" />בחר הכל</>}
                    </button>
                    {selectedCount > 0 && (
                      <>
                        <span className="text-gray-300">|</span>
                        <button
                          onClick={clearSelection}
                          className="text-sm text-gray-500 hover:text-gray-700 transition-colors"
                        >
                          נקה
                        </button>
                      </>
                    )}

                    {/* Group toggle chips */}
                    <span className="text-gray-300 mx-1">|</span>
                    {newProducts.length > 0 && (
                      <GroupChip
                        label="חדשים"
                        count={newProducts.length}
                        allSelected={newProducts.every(c => selected.has(c.source.external_id))}
                        colorActive="bg-emerald-100 text-emerald-800 border-emerald-300"
                        colorInactive="bg-white text-emerald-700 border-emerald-200 hover:bg-emerald-50"
                        onClick={() => toggleGroup(newProducts)}
                      />
                    )}
                    {updatedProducts.length > 0 && (
                      <GroupChip
                        label="עם עדכון"
                        count={updatedProducts.length}
                        allSelected={updatedProducts.every(c => selected.has(c.source.external_id))}
                        colorActive="bg-amber-100 text-amber-800 border-amber-300"
                        colorInactive="bg-white text-amber-700 border-amber-200 hover:bg-amber-50"
                        onClick={() => toggleGroup(updatedProducts)}
                      />
                    )}
                    {unchangedProducts.length > 0 && (
                      <GroupChip
                        label="ללא שינוי"
                        count={unchangedProducts.length}
                        allSelected={unchangedProducts.every(c => selected.has(c.source.external_id))}
                        colorActive="bg-gray-200 text-gray-800 border-gray-400"
                        colorInactive="bg-white text-gray-600 border-gray-200 hover:bg-gray-50"
                        onClick={() => toggleGroup(unchangedProducts)}
                      />
                    )}
                    {draftProducts.length > 0 && (
                      <GroupChip
                        label="דראפט"
                        count={draftProducts.length}
                        allSelected={draftProducts.every(c => selected.has(c.source.external_id))}
                        colorActive="bg-sky-100 text-sky-800 border-sky-300"
                        colorInactive="bg-white text-sky-700 border-sky-200 hover:bg-sky-50"
                        onClick={() => toggleGroup(draftProducts)}
                      />
                    )}
                    {archivedProducts.length > 0 && (
                      <GroupChip
                        label="בארכיון"
                        count={archivedProducts.length}
                        allSelected={archivedProducts.every(c => selected.has(c.source.external_id))}
                        colorActive="bg-rose-100 text-rose-800 border-rose-300"
                        colorInactive="bg-white text-rose-700 border-rose-200 hover:bg-rose-50"
                        onClick={() => toggleGroup(archivedProducts)}
                      />
                    )}

                    {selectedCount > 0 && (
                      <span className="px-2.5 py-1 bg-blue-100 text-blue-700 text-xs font-bold rounded-full mr-1">
                        {selectedCount} נבחרו
                      </span>
                    )}
                  </div>

                  {/* ── TOP import button ── */}
                  <ImportButton
                    selectedCount={selectedCount}
                    isImporting={isImporting}
                    generatingSku={generatingSku}
                    onClick={handleImportClick}
                    size="sm"
                  />
                </div>

                {filtered.length === 0 ? (
                  <div className="flex flex-col items-center justify-center py-16 text-center text-gray-400 gap-2">
                    <Search className="w-8 h-8" />
                    <p className="font-medium">לא נמצאו מוצרים תואמים לחיפוש</p>
                  </div>
                ) : (
                  <div className="overflow-x-auto">
                    <table className="w-full min-w-[640px]">
                      <thead>
                        <tr className="border-b border-gray-100 text-right bg-gray-50/50">
                          <th className="w-10 px-3 py-3"></th>
                          <th className="w-14 px-2 py-3"></th>
                          <th className="px-4 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wide">
                            שם המוצר
                          </th>
                          <th className="px-4 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wide">
                            מקט (SKU)
                          </th>
                          <th className="px-4 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wide text-right">
                            מחיר
                          </th>
                        </tr>
                      </thead>
                      <tbody>

                        {/* ── Section 1: New products ── */}
                        {newProducts.length > 0 && (
                          <>
                            <SectionHeader
                              icon={<Plus className="w-4 h-4 text-emerald-600" />}
                              label="מוצרים חדשים"
                              count={newProducts.length}
                              color="bg-emerald-50/70 text-emerald-800"
                            />
                            {newProducts.map(c => (
                              <ProductRow
                                key={c.source.external_id}
                                classified={c}
                                checked={selected.has(c.source.external_id)}
                                onToggle={() => toggleOne(c.source.external_id)}
                                isDuplicate={duplicateSkuMap.has(c.source.external_id)}
                              />
                            ))}
                          </>
                        )}

                        {/* ── Section 2: Updated products ── */}
                        {updatedProducts.length > 0 && (
                          <>
                            <SectionHeader
                              icon={<RefreshCcw className="w-4 h-4 text-amber-600" />}
                              label="מוצרים קיימים עם עדכון"
                              count={updatedProducts.length}
                              color="bg-amber-50/70 text-amber-800"
                            />
                            {updatedProducts.map(c => (
                              <ProductRow
                                key={c.source.external_id}
                                classified={c}
                                checked={selected.has(c.source.external_id)}
                                onToggle={() => toggleOne(c.source.external_id)}
                                isDuplicate={duplicateSkuMap.has(c.source.external_id)}
                              />
                            ))}
                          </>
                        )}

                        {/* ── Section 3: Unchanged products ── */}
                        {unchangedProducts.length > 0 && (
                          <>
                            <SectionHeader
                              icon={<CheckCircle2 className="w-4 h-4 text-gray-400" />}
                              label="מוצרים קיימים ללא שינוי"
                              count={unchangedProducts.length}
                              color="bg-gray-50/70 text-gray-500"
                            />
                            {unchangedProducts.map(c => (
                              <ProductRow
                                key={c.source.external_id}
                                classified={c}
                                checked={selected.has(c.source.external_id)}
                                onToggle={() => toggleOne(c.source.external_id)}
                                isDuplicate={duplicateSkuMap.has(c.source.external_id)}
                              />
                            ))}
                          </>
                        )}

                      </tbody>
                    </table>
                  </div>
                )}

                {/* ── Import result / error banners ── */}
                {importState === 'done' && importResult && (
                  <div className="m-5 flex items-start gap-3 px-4 py-4 bg-emerald-50 border border-emerald-200 rounded-xl text-sm text-emerald-700">
                    <CheckCircle2 className="w-5 h-5 shrink-0 mt-0.5" />
                    <div>
                      <p className="font-semibold">
                        יובאו / עודכנו {importResult.imported_count} מוצרים בהצלחה!
                      </p>
                      {importResult.skipped_count > 0 && (
                        <p className="text-emerald-600 mt-0.5 text-xs">
                          {importResult.skipped_count} מוצרים דולגו (שם ריק או שגיאה).
                        </p>
                      )}
                      <p className="text-emerald-500 text-xs mt-1">
                        מזהה עבודה: {importResult.job_id.slice(0, 8)}…
                      </p>
                    </div>
                  </div>
                )}

                {importState === 'error' && importError && (
                  <div className="m-5 flex items-start gap-3 px-4 py-4 bg-red-50 border border-red-200 rounded-xl text-sm text-red-700">
                    <AlertCircle className="w-5 h-5 shrink-0 mt-0.5" />
                    <div>
                      <p className="font-semibold">שגיאה בייבוא</p>
                      <p className="text-red-600 mt-0.5">{importError}</p>
                    </div>
                  </div>
                )}

                {/* ── Bottom action bar ── */}
                <div className="flex items-center justify-between gap-4 px-5 py-4 bg-gray-50 border-t border-gray-100 flex-wrap">
                  <div className="flex flex-col gap-2">
                    <p className="text-xs text-gray-400 flex items-center gap-1">
                      <Info className="w-3.5 h-3.5" />
                      מוצרים קיימים יעודכנו אוטומטית. מוצרים חדשים יתווספו לקטלוג.
                    </p>
                    <button
                      type="button"
                      onClick={() => setPriceInclVat(v => !v)}
                      className={`flex items-center gap-2 text-xs rounded-lg px-3 py-1.5 border
                                  transition-colors select-none w-fit
                                  ${priceInclVat
                                    ? 'bg-blue-50 border-blue-300 text-blue-700'
                                    : 'bg-white border-gray-200 text-gray-500 hover:border-gray-300'}`}
                    >
                      <Hash className="w-3.5 h-3.5 shrink-0" />
                      <span>האם המחירים המיובאים כוללים מע״מ?</span>
                      {priceInclVat
                        ? <span className="font-bold text-blue-700">כן ÷(1+מע״מ)</span>
                        : <span className="text-gray-400">לא (ברירת מחדל)</span>}
                    </button>
                  </div>

                  <ImportButton
                    selectedCount={selectedCount}
                    isImporting={isImporting}
                    generatingSku={generatingSku}
                    onClick={handleImportClick}
                  />
                </div>
              </>
            )}
          </div>
        )}

        {/* ════ PRE-FLIGHT MODALS ════ */}

        {preflightStep === 'scenarioA' && preflightResult && (
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm p-4">
            <div className="bg-white rounded-2xl shadow-2xl w-full max-w-lg p-6 space-y-5" dir="rtl">
              <div className="flex items-start gap-3">
                <div className="p-2.5 bg-amber-100 rounded-xl shrink-0">
                  <TriangleAlert className="w-5 h-5 text-amber-600" />
                </div>
                <div>
                  <h2 className="text-base font-bold text-gray-900">שים לב: מק״טים חיצוניים</h2>
                  <p className="text-sm text-gray-500 mt-1">
                    המוצרים שנבחרו מכילים מק״טים שהגיעו מהמערכת החיצונית.
                    מק״טים אלו עשויים שלא להתאים לשיטת יצירת המק״ט המוגדרת ב-Axelio.
                  </p>
                </div>
              </div>
              <div className="flex items-center gap-3 px-4 py-3 bg-amber-50 border border-amber-200 rounded-xl text-sm text-amber-800">
                <Hash className="w-4 h-4 text-amber-500 shrink-0" />
                <span>
                  <strong>{preflightResult.withSku.length}</strong> מוצרים עם מק״ט חיצוני ייובאו עם המק״ט המקורי.
                  המונה הפנימי של Axelio <strong>לא יושפע</strong>.
                </span>
              </div>
              {preflightResult.withSku.length > 0 && (
                <div className="space-y-1.5">
                  <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide">דוגמאות מק״טים שייובאו:</p>
                  <div className="flex flex-wrap gap-1.5">
                    {preflightResult.withSku.slice(0, 5).map(p => (
                      <span key={p.external_id}
                            className="px-2.5 py-1 bg-gray-100 text-gray-700 text-xs font-mono rounded-lg border border-gray-200">
                        {p.sku}
                      </span>
                    ))}
                    {preflightResult.withSku.length > 5 && (
                      <span className="px-2.5 py-1 text-gray-400 text-xs">
                        +{preflightResult.withSku.length - 5} נוספים…
                      </span>
                    )}
                  </div>
                </div>
              )}
              <div className={`flex items-center gap-2 px-3 py-2.5 rounded-xl text-xs border
                              ${priceInclVat
                                ? 'bg-blue-50 border-blue-200 text-blue-700'
                                : 'bg-gray-50 border-gray-200 text-gray-500'}`}>
                <Percent className="w-3.5 h-3.5 shrink-0" />
                {priceInclVat
                  ? 'המחירים ייובאו כוללי מע״מ — יחולקו לפי שיעור המע״מ הנוכחי של החברה לפני שמירה.'
                  : 'המחירים ייובאו כפי שהם (לא כוללים מע״מ).'}
              </div>
              <div className="flex items-center justify-end gap-3 pt-1">
                <button
                  onClick={() => setPreflightStep('none')}
                  className="px-4 py-2 text-sm text-gray-600 border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors"
                >
                  <X className="w-4 h-4 inline ml-1" />
                  ביטול
                </button>
                <button
                  onClick={handleScenarioAConfirm}
                  className="flex items-center gap-2 px-5 py-2 bg-blue-600 hover:bg-blue-700
                             text-white text-sm font-semibold rounded-lg transition-colors shadow-sm"
                >
                  <Download className="w-4 h-4" />
                  כן, ייבא עם המק״טים החיצוניים
                </button>
              </div>
            </div>
          </div>
        )}

        {(preflightStep === 'scenarioB' || preflightStep === 'generating') && preflightResult && (
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm p-4">
            <div className="bg-white rounded-2xl shadow-2xl w-full max-w-lg p-6 space-y-5" dir="rtl">
              <div className="flex items-start gap-3">
                <div className="p-2.5 bg-blue-100 rounded-xl shrink-0">
                  <AlertCircle className="w-5 h-5 text-blue-600" />
                </div>
                <div>
                  <h2 className="text-base font-bold text-gray-900">
                    {preflightResult.withoutSku.length} מוצרים ללא מק״ט
                  </h2>
                  <p className="text-sm text-gray-500 mt-1">
                    {preflightResult.withoutSku.length === resolvedBatch.length
                      ? 'לאף אחד מהמוצרים הנבחרים אין מק״ט.'
                      : `${preflightResult.withoutSku.length} מתוך ${resolvedBatch.length} מוצרים נבחרים חסרים מק״ט.`}
                    {' '}כיצד ברצונך להמשיך?
                  </p>
                </div>
              </div>
              <div className="space-y-1.5">
                <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide">מוצרים ללא מק״ט:</p>
                <div className="max-h-28 overflow-y-auto space-y-1">
                  {preflightResult.withoutSku.slice(0, 8).map(p => (
                    <div key={p.external_id}
                         className="flex items-center gap-2 px-3 py-1.5 bg-gray-50 rounded-lg text-sm text-gray-700">
                      <span className="w-2 h-2 rounded-full bg-red-400 shrink-0" />
                      <span className="truncate">{p.name}</span>
                    </div>
                  ))}
                  {preflightResult.withoutSku.length > 8 && (
                    <p className="text-xs text-gray-400 px-3">+{preflightResult.withoutSku.length - 8} נוספים…</p>
                  )}
                </div>
              </div>
              <div className="grid grid-cols-1 gap-3">
                <button
                  onClick={handleScenarioBGenerate}
                  disabled={generatingSku}
                  className="flex items-start gap-3 px-4 py-3.5 bg-blue-50 border-2 border-blue-400
                             rounded-xl text-right hover:bg-blue-100 transition-colors
                             disabled:opacity-60 disabled:cursor-not-allowed"
                >
                  <div className="p-1.5 bg-blue-500 rounded-lg shrink-0 mt-0.5">
                    {generatingSku
                      ? <Loader2 className="w-4 h-4 text-white animate-spin" />
                      : <Sparkles className="w-4 h-4 text-white" />}
                  </div>
                  <div>
                    <p className="text-sm font-semibold text-blue-800">
                      {generatingSku ? 'מייצר מק״טים…' : 'צור מק״טים אוטומטית'}
                    </p>
                    <p className="text-xs text-blue-600 mt-0.5">
                      המערכת תקצה מק״ט לפי הגדרות המק״ט הנוכחיות לכל {preflightResult.withoutSku.length} המוצרים
                      {preflightResult.withSku.length > 0 && ` ותייבא יחד עם ${preflightResult.withSku.length} המוצרים הנותרים`}.
                    </p>
                  </div>
                </button>

                {preflightResult.withSku.length > 0 && (
                  <button
                    onClick={handleScenarioBSkip}
                    disabled={generatingSku}
                    className="flex items-start gap-3 px-4 py-3.5 bg-gray-50 border-2 border-gray-200
                               rounded-xl text-right hover:bg-gray-100 transition-colors
                               disabled:opacity-60 disabled:cursor-not-allowed"
                  >
                    <div className="p-1.5 bg-gray-400 rounded-lg shrink-0 mt-0.5">
                      <Download className="w-4 h-4 text-white" />
                    </div>
                    <div>
                      <p className="text-sm font-semibold text-gray-700">דלג על מוצרים ללא מק״ט</p>
                      <p className="text-xs text-gray-500 mt-0.5">
                        ייבא רק את {preflightResult.withSku.length} המוצרים שכבר יש להם מק״ט.
                        {preflightResult.withoutSku.length} מוצרים ידולגו.
                      </p>
                    </div>
                  </button>
                )}

                <div className={`flex items-center gap-2 px-3 py-2.5 rounded-xl text-xs border
                                ${priceInclVat
                                  ? 'bg-blue-50 border-blue-200 text-blue-700'
                                  : 'bg-gray-50 border-gray-200 text-gray-500'}`}>
                  <Percent className="w-3.5 h-3.5 shrink-0" />
                  {priceInclVat
                    ? 'המחירים ייובאו כוללי מע״מ — יחולקו לפי שיעור המע״מ הנוכחי של החברה לפני שמירה.'
                    : 'המחירים ייובאו כפי שהם (לא כוללים מע״מ).'}
                </div>

                <button
                  onClick={() => setPreflightStep('none')}
                  disabled={generatingSku}
                  className="text-sm text-gray-500 hover:text-gray-700 transition-colors py-1
                             disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  ביטול — חזור לרשימה
                </button>
              </div>
            </div>
          </div>
        )}

        {/* ── Future sources teaser ── */}
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
          {[
            { icon: '🛒', label: 'WooCommerce', note: 'בקרוב' },
            { icon: '🔶', label: 'Magento',     note: 'בקרוב' },
            { icon: '📊', label: 'Excel / CSV', note: 'בקרוב' },
          ].map(t => (
            <div
              key={t.label}
              className="flex items-center gap-3 px-4 py-3 bg-white rounded-xl border border-dashed border-gray-200 opacity-60"
            >
              <span className="text-xl">{t.icon}</span>
              <div>
                <p className="text-sm font-semibold text-gray-500">{t.label}</p>
                <p className="text-xs text-gray-400">{t.note}</p>
              </div>
            </div>
          ))}
        </div>

      </div>
    </div>
  );
}
